'use client';

import { useMemo } from 'react';
import type { GeoBasedFeatures } from '@frameio/graphql-client/generated';
import { useGetGeoFeaturesQuery } from '../gql/generated/GetGeoFeatures';
import { useMeCachedQuery } from './useMeCachedQuery';

export type GeoFeatureHookProps = {
  feature: keyof Omit<GeoBasedFeatures, '__typename'>;
  isInitiallyPermitted?: boolean;
};

export type GeoFeatureHookResult = {
  isPermitted: boolean;
  loading: boolean;
  error: Error | null;
};

/**
 * Hook to check if a specific geo-based feature is enabled for the current user.
 *
 * @param feature - The feature to check
 * @param isInitiallyPermitted - Whether to show children while permissions are being checked (defaults to false)
 * @returns Returns an object containing:
 *   - isPermitted: boolean indicating if the feature is enabled
 *   - loading: boolean indicating if the permissions are being checked
 *   - error: Error object if there was an error fetching permissions
 *
 * @example
 * ```tsx
 * const { isPermitted, loading, error } = useGeoFeature({
 *   feature: 'canToggleSpeakerDiarization',
 *   isInitiallyPermitted: false
 * });
 * ```
 */
export const useGeoFeature = ({
  feature,
  isInitiallyPermitted = false,
}: GeoFeatureHookProps): GeoFeatureHookResult => {
  const { data: cachedMeData } = useMeCachedQuery();

  const isAuthenticatedUser =
    cachedMeData?.me?.__typename === 'AuthenticatedUser';

  const { data, error, loading } = useGetGeoFeaturesQuery({
    nextFetchPolicy: 'cache-only',
    skip: !isAuthenticatedUser,
  });

  const isPermitted = useMemo(() => {
    if (loading) return isInitiallyPermitted;
    if (error || !data) return isInitiallyPermitted;

    const geoBasedFeatures =
      data?.me?.__typename === 'AuthenticatedUser'
        ? data?.me?.geoBasedFeatures
        : null;

    if (!geoBasedFeatures) return isInitiallyPermitted;

    switch (feature) {
      case 'canToggleSpeakerDiarization':
        return (
          geoBasedFeatures?.canToggleSpeakerDiarization ?? isInitiallyPermitted
        );
      default:
        return isInitiallyPermitted;
    }
  }, [data, error, loading, feature, isInitiallyPermitted]);

  return {
    isPermitted,
    loading,
    error: error || null,
  };
};
